import type { WeightSampleLog } from '../../types/production'

import { getOfflineDb } from '../dbFactory'

export type WeightSampleLogListParams = {
  farmId?: string
  productionUnitId?: string
  lot?: string
  lotId?: string
  productionRowId?: string
  fromRecordedAt?: string
  toRecordedAt?: string
}

export class DexieWeightSampleLogRepo {
  async list(params: WeightSampleLogListParams = {}): Promise<WeightSampleLog[]> {
    const farmId = params.farmId?.trim() || undefined
    const productionUnitId = params.productionUnitId?.trim() || undefined
    const lot = params.lot?.trim() || undefined
    const lotId = params.lotId?.trim() || undefined
    const productionRowId = params.productionRowId?.trim() || undefined
    const fromRecordedAt = params.fromRecordedAt?.trim() || undefined
    const toRecordedAt = params.toRecordedAt?.trim() || undefined

    const db = getOfflineDb()
    const table = db.weightSampleLogs

    let base: WeightSampleLog[]

    if (farmId && productionUnitId) {
      base = await table.where('[farmId+productionUnitId]').equals([farmId, productionUnitId]).toArray()
    } else if (farmId) {
      base = await table.where('farmId').equals(farmId).toArray()
    } else if (productionUnitId) {
      base = await table.where('productionUnitId').equals(productionUnitId).toArray()
    } else {
      base = await table.toArray()
    }

    const fromTime = fromRecordedAt ? new Date(fromRecordedAt).getTime() : undefined
    const toTime = toRecordedAt ? new Date(toRecordedAt).getTime() : undefined

    return base
      .filter((log) => {
        if (lot && (log.lot ?? '') !== lot) return false
        if (lotId && (log.lotId ?? '') !== lotId) return false
        if (productionRowId && (log.productionRowId ?? '') !== productionRowId) return false

        if (fromTime !== undefined || toTime !== undefined) {
          const time = new Date(log.recordedAt).getTime()
          if (Number.isNaN(time)) return false
          if (fromTime !== undefined && time < fromTime) return false
          if (toTime !== undefined && time > toTime) return false
        }

        return true
      })
      .sort((left, right) => right.recordedAt.localeCompare(left.recordedAt))
  }

  async getById(id: string): Promise<WeightSampleLog | undefined> {
    return getOfflineDb().weightSampleLogs.get(id)
  }

  async upsert(log: WeightSampleLog): Promise<void> {
    await getOfflineDb().weightSampleLogs.put(log)
  }

  async upsertMany(logs: WeightSampleLog[]): Promise<void> {
    if (logs.length === 0) return
    await getOfflineDb().weightSampleLogs.bulkPut(logs)
  }

  async delete(id: string): Promise<void> {
    await getOfflineDb().weightSampleLogs.delete(id)
  }

  async deleteMany(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    await getOfflineDb().weightSampleLogs.bulkDelete(ids)
  }

  async clear(): Promise<void> {
    await getOfflineDb().weightSampleLogs.clear()
  }
}

export const dexieWeightSampleLogRepo = new DexieWeightSampleLogRepo()
